/**
 * caseSessionIndex — cross-references a recovered non-interactive IP against what the
 * CASE already knows about the session its tokens belong to.
 *
 * WHY THIS EXISTS
 * ---------------
 * The non-interactive feed carries no actions, only authentication. Judged alone, a
 * recovered IP is a bare fact ("224 tokens from an address nobody scored"). The thing
 * that makes it evidence is the SESSION: a session is created by an interactive sign-in
 * — which is audited and therefore already in the case — and then replayed
 * non-interactively, which is the half only the other feed holds. Neither side is
 * conclusive; joined on `sessionId` they are.
 *
 * Measured on `IR-20260818-Controller-001`: session `005d431a-2e3a-efca-8d94-884aadf0c559`
 * spans 10 IPs across the two feeds — the victim's own office IP (which CREATED it), the
 * compromise IP, four already-classified attacker IPs carrying the Jul 31 inbox-rule
 * creation, and the three IPs only the non-interactive feed can see.
 *
 * 🔴 THE SHARED SESSION IS CORROBORATION, NEVER ATTRIBUTION. A stolen token keeps the
 * victim's own session id — that is what makes it a stolen token — so the same session
 * legitimately carries the real user's activity. `maliciousIps` is therefore reported as
 * a COUNT OF NEIGHBOURS, and this module deliberately exposes no "this session is the
 * attacker's" flag. Attribution stays per-event, on the IP recorded against that event.
 * Building the flag would be one line and would be wrong.
 *
 * Session ids come from `SessionId` (sign-in) and `AADSessionId` (Exchange/SharePoint),
 * which Microsoft issues as ONE namespace for the two layers of a logical session — the
 * same rule `SessionsTab` and `CaseDetail.compromisedSessions` already follow. The
 * workload list is kept in step with `SessionsTab.SESSION_SOURCES`.
 */

const SESSION_SOURCES = [
  'SignInEvents',
  'ExchangeEvents',
  'SharePointEvents',
  'MessageTraceEvents',
  'UrlClickEvents',
  'GeneralEvents',
  'AzureActivityEvents',
  'RiskDetectionEvents',
  'SecurityAlertEvents',
];

/**
 * Operations worth naming back to the analyst when they appear on a shared session.
 * Deliberately a SHORT list of state-changing, attacker-characteristic actions — not
 * "everything that is not a read". A session that merely read mail is not a finding, and
 * padding this list would bury the one line that is.
 */
const NOTABLE_OPS = new Set([
  'new-inboxrule', 'set-inboxrule', 'remove-inboxrule', 'updateinboxrules',
  'modifyfolderpermissions', 'add-mailboxpermission', 'remove-mailboxpermission',
  'set-mailbox', 'add-recipientpermission', 'set-mailboxjunkemailconfiguration',
  'new-transportrule', 'set-transportrule',
  'add member to role', 'add app role assignment grant to user',
  'consent to application', 'add service principal', 'add service principal credentials',
  'add owner to application', 'update application', 'update user',
  'add-mailboxfolderpermission', 'set-mailboxcalendarconfiguration',
  'disable strong authentication', 'update authorization policy',
]);

const ts = (v) => {
  if (!v) return NaN;
  const d = Date.parse(v);
  return Number.isFinite(d) ? d : NaN;
};

const stripPort = (ip) => {
  const s = String(ip || '').trim();
  if (!s) return '';
  if (s.startsWith('[')) return s.slice(1, s.indexOf(']') > 0 ? s.indexOf(']') : undefined);
  // IPv4 with port only — an IPv6 literal has many colons and must not be truncated.
  const parts = s.split(':');
  return parts.length === 2 ? parts[0] : s;
};

/** Every IP an event can be attributed to, including service-proxy-resolved ones. */
function eventIps(evt) {
  const out = [];
  const push = (v) => { const c = stripPort(v); if (c) out.push(c); };
  push(evt.IPAddress);
  push(evt.InferredIP);
  push(evt.ResolvedUserIP);
  if (evt.AllResolvedIPs) String(evt.AllResolvedIPs).split(',').forEach(push);
  return out;
}

/**
 * Build `{ [sessionId]: { ips, maliciousIps, sensitiveOps, eventCount } }` from the case's
 * own events. `ipClassifications` is the analyst's per-case map, so the malicious set is
 * THEIR judgement, not a reputation feed — the same source the IP table colours from.
 */
export function buildSessionIndex(results, ipClassifications = {}) {
  const index = {};
  if (!results) return index;

  const malicious = new Set(
    Object.entries(ipClassifications || {})
      .filter(([, v]) => v === 'malicious')
      .map(([k]) => String(k).toLowerCase()),
  );

  for (const listKey of SESSION_SOURCES) {
    const events = results[listKey];
    if (!Array.isArray(events)) continue;

    for (const evt of events) {
      const sid = evt.SessionId || evt.AADSessionId;
      if (!sid) continue;

      let s = index[sid];
      if (!s) {
        s = { sessionId: sid, _ips: new Set(), _mal: new Set(), _ops: new Set(), eventCount: 0 };
        index[sid] = s;
      }
      s.eventCount += 1;

      for (const ip of eventIps(evt)) {
        s._ips.add(ip);
        if (malicious.has(ip.toLowerCase())) s._mal.add(ip);
      }

      const op = String(evt.Operation || evt.OperationName || '').trim();
      if (op && NOTABLE_OPS.has(op.toLowerCase())) s._ops.add(op);
    }
  }

  for (const s of Object.values(index)) {
    s.ips = Array.from(s._ips).sort();
    s.maliciousIps = Array.from(s._mal).sort();
    s.sensitiveOps = Array.from(s._ops).sort();
    delete s._ips; delete s._mal; delete s._ops;
  }
  return index;
}

/**
 * Counts the case's OWN events inside a recovered IP's token window, and how many came
 * from IPs the analyst classified malicious.
 *
 * The load-bearing case is the ZERO: on the reference case `43.166.208.95` refreshed a
 * token 224 times across Jul 19–24 while the case shows a full week of ordinary work from
 * the office IP and NOT ONE attacker-attributed event. Without this line those six days
 * read as clean. Note it is deliberately "what the case shows", not "what happened" —
 * events the analyst filtered out are still counted, because this is a statement about
 * the underlying record, not about the current view.
 */
export function makeActivityInWindow(results) {
  const rows = [];
  if (results) {
    for (const listKey of SESSION_SOURCES) {
      const events = results[listKey];
      if (!Array.isArray(events)) continue;
      for (const evt of events) {
        // 🔴 `EventTimeUTC` is the field. v1.148.0 read CreationTime/Timestamp/EventTime,
        // none of which exist on a normalized case event, so EVERY timestamp parsed as NaN,
        // every row was skipped, and the continuity line reported "0 events ... the case
        // reads as normal" on every IP of every case - a reassurance that was never
        // computed. Fabricating calm is worse than saying nothing.
        const t = ts(evt.EventTimeUTC || evt.CreationTime || evt.Timestamp || evt.EventTime);
        if (!Number.isFinite(t)) continue;
        rows.push({ t, ips: eventIps(evt) });
      }
    }
  }
  rows.sort((a, b) => a.t - b.t);

  // If NOTHING parsed, we cannot count - and "0 events" would be a claim, not a blank.
  // Return null so the caller renders nothing at all. This is the guard that would have
  // made the v1.148.0 field-name bug visible instead of silently reassuring.
  const parsedAny = rows.length > 0;

  return (fromIso, toIso, ipClassifications = {}) => {
    if (!parsedAny) return null;
    const from = ts(fromIso);
    const to = ts(toIso);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return null;

    const malicious = new Set(
      Object.entries(ipClassifications || {})
        .filter(([, v]) => v === 'malicious')
        .map(([k]) => String(k).toLowerCase()),
    );

    let total = 0;
    let mal = 0;
    for (const r of rows) {
      if (r.t < from) continue;
      if (r.t > to) break;
      total += 1;
      if (r.ips.some((ip) => malicious.has(ip.toLowerCase()))) mal += 1;
    }
    return { total, malicious: mal };
  };
}

export { SESSION_SOURCES as CASE_SESSION_SOURCES, NOTABLE_OPS };
